import { getAuthHeaders } from './network_calls';
import { usageApiUrl } from './usage';

export async function gambitAccount<T>(body: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const headers = await getAuthHeaders();
  if (!headers.Authorization) throw Object.assign(new Error('Sign in to continue.'), { status: 401 });
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  signal?.addEventListener('abort', cancel, { once: true });
  const timeout = setTimeout(cancel, 30000);
  try {
    const response = await fetch(usageApiUrl('/gambit'), { method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
    const data = await response.json().catch(error => { if (response.ok) throw error; return {}; });
    if (!response.ok) throw Object.assign(new Error(data?.message || 'Could not update your account. Please try again.'),
      { status: response.status, retryable: response.status === 429 || response.status >= 500 });
    return data;
  } catch (error) {
    if (signal?.aborted) throw error;
    if (controller.signal.aborted) throw Object.assign(new Error('The account check took too long. Please retry.'), { retryable: true });
    if (error instanceof TypeError) throw Object.assign(error, { retryable: true });
    throw error;
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', cancel); }
}
export async function loadGambitAccess(signal?: AbortSignal) {
  const data = await gambitAccount<{ allowed: boolean; userId: string }>({ operation: 'access' }, signal);
  if (typeof data?.allowed !== 'boolean' || typeof data?.userId !== 'string' || !data.userId)
    throw new Error('Could not verify your membership.');
  return data;
}
function validatePins(data: { pins: string[] }) {
  if (!Array.isArray(data?.pins) || data.pins.length > 100 || data.pins.some(pin => typeof pin !== 'string' || !/^(story|draft):[a-zA-Z0-9_-]{1,100}$/.test(pin)))
    throw new Error('Could not load your pins. Please retry.');
  return data;
}
export const loadStoryPins = async (userId: string, signal?: AbortSignal) => validatePins(
  await gambitAccount<{ pins: string[] }>({ operation: 'pins', userId }, signal));
export const saveStoryPin = async (userId: string, kind: 'story' | 'draft', id: string, pinned: boolean) => validatePins(
  await gambitAccount<{ pins: string[] }>({ operation: 'pins', action: 'set', userId, kind, id, pinned }));
